import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../Context/AuthContext";
import { db } from "../Context/firebaseConfig";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import ChatSidebar from "../Components/ChatSidebar";
import ChatWindow from "../Components/ChatWindow";
import ChatEmptyState from "../Components/ChatEmptyState";
import TheNav from "../Components/navbar";
import { useSearchParams } from "react-router-dom";
import SEO from "../Components/SEO";

const formatConversationTime = (timestamp) => {
  if (!timestamp) return "";

  const date =
    typeof timestamp.toDate === "function"
      ? timestamp.toDate()
      : timestamp instanceof Date
        ? timestamp
        : new Date(timestamp);

  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return "now";
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const ChatPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const selectedChat = searchParams.get("conversationId");
  const draftMessage = searchParams.get("draft") || "";
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [queryText, setQueryText] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setLoadingConversations(false);
      return undefined;
    }

    setLoadingConversations(true);
    const chatsQuery = query(
      collection(db, "Users", currentUser.uid, "chats"),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(
      chatsQuery,
      (snapshot) => {
        const chatList = snapshot.docs.map((chatDoc) => ({
          id: chatDoc.id,
          ...chatDoc.data(),
        }));
        setConversations(chatList);
        setLoadingConversations(false);
      },
      (error) => {
        console.error("Error loading chats:", error);
        setLoadingConversations(false);
      }
    );

    return unsubscribe;
  }, [currentUser]);

  useEffect(() => {
    if (!selectedChat) {
      setMessages([]);
      return undefined;
    }

    const chatDoc = doc(db, "chats", selectedChat);
    const unsubscribe = onSnapshot(
      chatDoc,
      (snap) => {
        const data = snap.data() || {};
        setMessages(data.messages || []);
      },
      (error) => {
        console.error("Error loading chat messages:", error);
        setMessages([]);
      }
    );

    return unsubscribe;
  }, [selectedChat]);

  useEffect(() => {
    let isMounted = true;

    const fetchAllUserDetails = async () => {
      if (!currentUser || conversations.length === 0) {
        if (isMounted) setUserDetails({});
        return;
      }

      const details = {};

      try {
        for (const conv of conversations) {
          const [idA, idB] = conv.id.split("_");
          const friendId = idA === currentUser.uid ? idB : idA;

          if (friendId && !details[friendId]) {
            const userDocRef = doc(db, "Users", friendId);
            const userSnapshot = await getDoc(userDocRef);
            if (userSnapshot.exists()) details[friendId] = userSnapshot.data();
          }
        }

        const currentUserDocRef = doc(db, "Users", currentUser.uid);
        const currentUserSnapshot = await getDoc(currentUserDocRef);
        if (currentUserSnapshot.exists()) {
          details[currentUser.uid] = currentUserSnapshot.data();
        }

        if (isMounted) setUserDetails(details);
      } catch (error) {
        console.error("Error loading chat user details:", error);
        if (isMounted) {
          setUserDetails((previousDetails) => previousDetails);
        }
      }
    };

    fetchAllUserDetails();

    return () => {
      isMounted = false;
    };
  }, [conversations, currentUser]);

  const sidebarConversations = useMemo(() => {
    if (!currentUser) return [];

    return conversations.map((conversation) => {
      const [idA, idB] = conversation.id.split("_");
      const friendId = idA === currentUser.uid ? idB : idA;
      const friendDetails = userDetails[friendId] || {};

      return {
        id: conversation.id,
        username: friendDetails.profileInfo?.username || "Loading...",
        avatar: friendDetails.profileInfo?.profilePicUrl || null,
        lastMessage: conversation.lastMessage || "",
        timestamp: formatConversationTime(conversation.timestamp),
        unreadCount: conversation.unreadCount,
        online: friendDetails.online,
      };
    });
  }, [conversations, currentUser, userDetails]);

  const handleSelectConversation = (chatId) => {
    setSearchParams({ conversationId: chatId });
  };

  const handleBack = () => {
    setSearchParams({});
  };

  return (
    <>
      <SEO
        title="Messages | Kre8"
        description="Private Kre8 messages for creators and editors."
        canonicalPath="/chat"
        noindex
      />

      <TheNav />
      <main className="min-h-screen bg-[#07060d] text-white">
        <div className="mx-auto flex h-[calc(100vh-72px)] w-full max-w-[1500px] overflow-hidden">
          <aside
            className={`w-full border-r border-white/10 md:block md:w-[340px] md:shrink-0 lg:w-[380px] ${
              selectedChat ? "hidden" : "block"
            }`}
          >
            <ChatSidebar
              conversations={sidebarConversations}
              selectedId={selectedChat}
              onSelect={handleSelectConversation}
              loading={loadingConversations}
              query={queryText}
              onQueryChange={setQueryText}
            />
          </aside>

          <section
            className={`min-w-0 flex-1 ${
              selectedChat ? "block" : "hidden md:block"
            }`}
          >
            {selectedChat ? (
              <ChatWindow
                messages={messages}
                currentUser={currentUser}
                userDetails={userDetails}
                selectedChat={selectedChat}
                initialDraft={draftMessage}
                onBack={handleBack}
              />
            ) : (
              <ChatEmptyState />
            )}
          </section>
        </div>
      </main>
    </>
  );
};

export default ChatPage;
